import React from 'react';
import { IonApp, IonContent, IonPage } from '@ionic/react';
import { Helmet } from 'react-helmet-async';
import Header from 'layouts/Header';
import '../PrivacyPage/PrivacyPage.css';

const TermsPage = () => {
  return (
    <IonApp>
      <Helmet>
        <title>Terms of Service - Zibby</title>
        <meta name="description" content="Zibby Terms of Service - Read our terms and conditions for using Zibby's AI-powered software testing and development platform." />
      </Helmet>
      <IonPage>
        <Header user={null} />
        <IonContent className="privacy-ion-content">
          <div className="privacy-page">
      <div className="privacy-container">
        <header className="privacy-header">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: February 10, 2026</p>
        </header>

        <div className="privacy-content">
          <section className="privacy-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using Zibby ("the Service"), operated by Zibby Pty Ltd ("we," "our," or "us"), 
              you accept and agree to be bound by the terms and conditions of this agreement. If you do not agree 
              to these terms, please do not use the Service.
            </p>
            <p>
              These Terms of Service, together with our <a href="/privacy">Privacy Policy</a>, govern your use 
              of the Service and form a legally binding agreement between you and Zibby Pty Ltd.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. Description of Service</h2>
            <p>
              Zibby is an AI-powered software testing and development platform that helps engineering teams 
              automate quality assurance, manage project workflows, and accelerate software delivery. 
              The Service includes, but is not limited to:
            </p>
            <ul>
              <li>AI-driven ticket analysis and test case generation from project management tools (e.g. Jira, Linear)</li>
              <li>Automated test execution using browser-based testing frameworks</li>
              <li>AI-powered code implementation and pull request generation</li>
              <li>Visual workflow automation and graph-based test orchestration</li>
              <li>Integration with third-party development tools, source control platforms, and CI/CD pipelines</li>
              <li>Project management dashboards, execution reports, and analytics</li>
              <li>Team collaboration features including role-based access and invite management</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. User Accounts</h2>
            <p>
              To access the Service, you must create an account using a supported authentication provider 
              (Google, Microsoft, Facebook, or Apple). You are responsible for:
            </p>
            <ul>
              <li>Maintaining the confidentiality and security of your account credentials</li>
              <li>All activities that occur under your account, including actions by team members you invite</li>
              <li>Notifying us immediately of any unauthorized use of your account</li>
              <li>Ensuring that any information you provide is accurate, current, and complete</li>
            </ul>
            <p>
              We reserve the right to suspend or terminate accounts that violate these Terms or that 
              we reasonably believe pose a security risk.
            </p>
          </section>

          <section className="privacy-section">
            <h2>4. Subscriptions and Billing</h2>
            <p>
              Certain features of the Service require a paid subscription. By subscribing to a paid plan, you agree to the following:
            </p>
            <ul>
              <li>Subscription fees are billed in advance on a recurring basis (monthly or annually) as selected at the time of purchase</li>
              <li>All fees are non-refundable unless otherwise required by applicable law or stated in our refund policy</li>
              <li>We may change subscription pricing with at least 30 days' prior notice; continued use after the price change takes effect constitutes acceptance</li>
              <li>You are responsible for providing valid and up-to-date payment information</li>
              <li>Failure to pay may result in suspension or termination of your access to paid features</li>
            </ul>
            <p>
              Payment processing is handled by Stripe. Your payment information is subject to 
              <a href="https://stripe.com/legal" target="_blank" rel="noopener noreferrer">Stripe's Terms of Service</a>.
            </p>
          </section>

          <section className="privacy-section">
            <h2>5. Acceptable Use</h2>
            <p>You agree not to use the Service to:</p>
            <ul>
              <li>Violate any applicable local, state, national, or international laws or regulations</li>
              <li>Test, scan, or probe systems or networks that you do not own or have explicit authorization to test</li>
              <li>Generate, store, or transmit malicious code, exploits, or harmful software through the platform</li>
              <li>Infringe on or misappropriate any intellectual property rights of third parties</li>
              <li>Attempt to gain unauthorized access to our systems, other user accounts, or connected services</li>
              <li>Interfere with, disrupt, or create an undue burden on the Service or its infrastructure</li>
              <li>Use the Service to engage in competitive benchmarking or to build a competing product</li>
              <li>Resell, sublicense, or share your account access without our prior written consent</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>6. Intellectual Property</h2>
            <p>
              The Service, including its software, design, branding, documentation, and AI models, is owned by 
              Zibby Pty Ltd and is protected by Australian and international copyright, trademark, and other 
              intellectual property laws.
            </p>
            <p>
              You retain full ownership of any code, test cases, configurations, and other content you create 
              or upload through the Service ("Your Content"). By using the Service, you grant us a limited, 
              non-exclusive license to use, process, and store Your Content solely as necessary to provide 
              and improve the Service.
            </p>
            <p>
              AI-generated outputs (such as test cases, code suggestions, and analysis reports) produced by 
              the Service based on Your Content are owned by you, subject to any applicable third-party licenses 
              on the underlying code or data.
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. Third-Party Integrations</h2>
            <p>
              The Service integrates with third-party platforms and services, including but not limited to:
            </p>
            <ul>
              <li><strong>Project Management:</strong> Jira, Linear, and other issue trackers</li>
              <li><strong>Source Control:</strong> GitHub, GitLab, Bitbucket</li>
              <li><strong>Authentication:</strong> Google, Microsoft, Facebook, Apple</li>
              <li><strong>AI Providers:</strong> OpenAI and other AI/ML service providers</li>
            </ul>
            <p>
              Your use of these integrated services is subject to their respective terms of service and privacy 
              policies. We are not responsible for the availability, accuracy, or conduct of any third-party service. 
              You authorize us to access and interact with these services on your behalf when you connect them.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. Service Availability and Support</h2>
            <p>
              We strive to maintain high availability of the Service but do not guarantee uninterrupted or error-free operation. 
              The Service may be temporarily unavailable due to maintenance, updates, or circumstances beyond our control.
            </p>
            <p>
              We will make commercially reasonable efforts to notify you of planned maintenance in advance. 
              Support is available via email and in accordance with your subscription plan.
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Disclaimers</h2>
            <p>
              The Service is provided "as is" and "as available" without warranties of any kind, whether express 
              or implied. We specifically disclaim all implied warranties, including but not limited to:
            </p>
            <ul>
              <li>Merchantability and fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
              <li>Accuracy, reliability, or completeness of AI-generated content, test results, or code suggestions</li>
              <li>Uninterrupted, timely, secure, or error-free operation</li>
            </ul>
            <p>
              AI-generated test cases, code, and analysis should be reviewed by qualified personnel before use 
              in production environments. We do not guarantee the correctness or completeness of any AI output.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, Zibby Pty Ltd and its officers, directors, employees, 
              and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, 
              including but not limited to loss of profits, data, business opportunities, or goodwill, arising out of 
              or relating to your use of or inability to use the Service.
            </p>
            <p>
              Our total aggregate liability for all claims arising from or relating to the Service shall not exceed 
              the total amount you paid to us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Zibby Pty Ltd from and against any claims, damages, 
              losses, liabilities, and expenses (including reasonable legal fees) arising from:
            </p>
            <ul>
              <li>Your use of the Service in violation of these Terms</li>
              <li>Your Content or any data you process through the Service</li>
              <li>Your violation of any applicable law or third-party rights</li>
              <li>Unauthorized testing or scanning of systems using the Service</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>12. Termination</h2>
            <p>
              Either party may terminate this agreement at any time. You may cancel your account through the 
              account settings or by contacting us. We may suspend or terminate your access immediately if:
            </p>
            <ul>
              <li>You breach any provision of these Terms</li>
              <li>Your use poses a security risk to the Service or other users</li>
              <li>Required by law or regulation</li>
              <li>Your account has been inactive for an extended period</li>
            </ul>
            <p>
              Upon termination, your right to use the Service ceases immediately. You may request export of 
              Your Content for up to 30 days after termination, after which it may be permanently deleted.
            </p>
          </section>

          <section className="privacy-section">
            <h2>13. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of material changes by 
              posting the updated Terms on this page, updating the "Last Updated" date, and where practicable, 
              sending an email notification. Your continued use of the Service after changes take effect 
              constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section className="privacy-section">
            <h2>14. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of the State of New South Wales, 
              Australia. Any disputes arising under or in connection with these Terms shall be subject to the 
              exclusive jurisdiction of the courts of New South Wales, Australia.
            </p>
            <p>
              Before initiating formal proceedings, both parties agree to attempt to resolve disputes through 
              good-faith negotiation for a period of at least 30 days.
            </p>
          </section>

          <section className="privacy-section">
            <h2>15. General Provisions</h2>
            <ul>
              <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy, constitute the entire agreement between you and Zibby Pty Ltd regarding the Service.</li>
              <li><strong>Severability:</strong> If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.</li>
              <li><strong>Assignment:</strong> You may not assign or transfer your rights under these Terms without our prior written consent. We may assign our rights and obligations without restriction.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>16. Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> contact@zibby.app</p>
              <p><strong>Website:</strong> <a href="https://zibby.app">https://zibby.app</a></p>
            </div>
          </section>
          </div>
        </div>
      </div>
        </IonContent>
      </IonPage>
    </IonApp>
  );
};

export default TermsPage;
